import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { EvtSectionHeader } from "components/header";
import QueryError from "components/query-error";
import { EvtStatsCardPlaceholder } from "components/stats/placeholder";
import { EvtStatsCard } from "components/stats/display-card";
import { DisplayPercentage } from "components/display/percentage";
import { DisplayAmount } from "components/display/amount";
import { DisplayHours } from "components/display/hours";
import { usePermissions } from "contexts/permission";
import { EvtLockedSection } from "components/stats/locked-section";
import { useFetch } from "@/hooks/useFetch";
import { useProject } from "../../../context";
import { fetchProjectRevenueCalculation } from "../../api";
import { DASHBOARD_STATS_INFO } from "../constants";
import {
  DASHBOARD_VALIDATION_TITLE,
  getDashboardValidationIssues,
} from "@/modules/work/shared/dashboard-validation";
import CalendarDatePicker from "ui/components/comp-507";

const StatsPlaceholder = ({ canReadAmount }) => (
  <>
    <section>
      <EvtSectionHeader title="Financials" />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
        <EvtStatsCardPlaceholder />
        {canReadAmount &&
          Array.from({ length: 8 }, (_, i) => (
            <EvtStatsCardPlaceholder key={i} />
          ))}
      </div>
    </section>
    <section>
      <EvtSectionHeader title="Delivery" />
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6">
        <EvtStatsCardPlaceholder />
      </div>
    </section>
  </>
);

export const ProjectDashboardStatsSection = () => {
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  });
  const { id } = useProject();
  const { project_slug: projectSlug } = useParams();
  const permissions = usePermissions();

  const canReadAmount = permissions.hasFieldAccess(
    "billables",
    "read",
    "amount",
  );

  const isDateRangeComplete = Boolean(dateRange?.from && dateRange?.to);
  const startDate = isDateRangeComplete ? dateRange.from : undefined;
  const endDate = isDateRangeComplete ? dateRange.to : undefined;

  const { data, isLoading, isError, refetch } = useFetch({
    queryKey: [
      "revenue-calculation-project-dashboard",
      id,
      startDate,
      endDate,
    ],
    queryFn: () =>
      fetchProjectRevenueCalculation({
        projectId: id,
        startDate,
        endDate,
      }),
    enabled: !!id,
  });

  if (isLoading) {
    return <StatsPlaceholder canReadAmount={canReadAmount} />;
  }
  const validationIssues = getDashboardValidationIssues(
    data?.validation,
    "project",
    projectSlug,
  );
  if (validationIssues.length) {
    return (
      <EvtLockedSection
        title={DASHBOARD_VALIDATION_TITLE}
        items={validationIssues}
      >
        <StatsPlaceholder canReadAmount={canReadAmount} />
      </EvtLockedSection>
    );
  }
  if (isError) {
    return (
      <>
        {canReadAmount && (
          <section className="mt-3.5">
            <EvtSectionHeader title="Financials" />
            <div className="px-4 md:px-6 mt-2.5">
              <QueryError
                message="Failed to load dashboard metrics."
                onRetry={refetch}
              />
            </div>
          </section>
        )}
        <section className="mt-3.5">
          <EvtSectionHeader title="Delivery" />
          <div className="px-4 md:px-6 mt-2.5">
            <QueryError
              message="Failed to load dashboard metrics."
              onRetry={refetch}
            />
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section>
        <EvtSectionHeader title="Financials">
          <CalendarDatePicker date={dateRange} setDate={setDateRange} />
        </EvtSectionHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
          <EvtStatsCard
            label="Project Health"
            value={
              <DisplayPercentage
                value={data?.margin_achievement_percentage}
                precision={0}
              />
            }
            info={DASHBOARD_STATS_INFO.budgetVsAchieved}
          />
          {canReadAmount && (
            <>
              <EvtStatsCard
                label="Total Revenue"
                value={<DisplayAmount value={data?.allocated_revenue} />}
                info={DASHBOARD_STATS_INFO.allocatedRevenue}
              />
              <EvtStatsCard
                label="Billable Revenue"
                value={<DisplayAmount value={data?.billable_revenue} />}
                info={DASHBOARD_STATS_INFO.billableRevenue}
              />
              <EvtStatsCard
                label="Billed Revenue"
                value={<DisplayAmount value={data?.billed_revenue} />}
                info={DASHBOARD_STATS_INFO.billedRevenue}
              />
              <EvtStatsCard
                label="Net Cost"
                value={<DisplayAmount value={data?.net_cost} />}
                info={DASHBOARD_STATS_INFO.netCost}
              />
              <EvtStatsCard
                label="Achieved Margin"
                value={<DisplayAmount value={data?.achieved_margin_inr} />}
                info={DASHBOARD_STATS_INFO.achievedRevenue}
              />
              <EvtStatsCard
                label="Inventory"
                value={<DisplayAmount value={data?.inventory} />}
                info={DASHBOARD_STATS_INFO.inventory}
              />
              <EvtStatsCard
                label="Prepaid/Advance"
                value={<DisplayAmount value={data?.prepaid} />}
                info={DASHBOARD_STATS_INFO.prepaid}
              />
              <EvtStatsCard
                label="Backlog"
                value={<DisplayAmount value={data?.backlog} />}
                info={DASHBOARD_STATS_INFO.backlog}
              />
            </>
          )}
        </div>
      </section>

      <section>
        <EvtSectionHeader title="Delivery" />
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 gap-4 md:px-6 mt-2.5">
          <EvtStatsCard
            label="Approved vs Logged"
            value={
              <>
                <DisplayHours value={data?.approved_hours} hoursOnly /> /{" "}
                <DisplayHours value={data?.logged_hours} hoursOnly />
              </>
            }
            info={DASHBOARD_STATS_INFO.approvedVsLogged}
          />
        </div>
      </section>
    </>
  );
};
